import { useEffect } from 'react';
import L from 'leaflet';
import { MapContainer, Marker, TileLayer, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

export type MapPoint = {
  id: string;
  name: string;
  lat: number;
  lng: number;
  primary?: boolean;
};

const INDIA_CENTER: [number, number] = [22.5, 79];

function pinIcon(primary: boolean): L.DivIcon {
  const size = primary ? 34 : 26;
  const fill = primary ? '#1F5A47' : '#339274';
  const html = `
    <svg width="${size}" height="${size * 1.32}" viewBox="0 0 26 34" xmlns="http://www.w3.org/2000/svg">
      <path d="M13 0C5.8 0 0 5.7 0 12.8 0 22.4 13 34 13 34s13-11.6 13-21.2C26 5.7 20.2 0 13 0z" fill="${fill}" stroke="#0B3B2E" stroke-width="1.2"/>
      <circle cx="13" cy="12.5" r="5" fill="#F8F2E2" stroke="#C9A961" stroke-width="1.5"/>
    </svg>`;
  return L.divIcon({
    className: '',
    html,
    iconSize: [size, size * 1.32],
    iconAnchor: [size / 2, size * 1.32],
    tooltipAnchor: [0, -size],
  });
}

function FitBounds({ points }: { points: MapPoint[] }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 9);
      return;
    }
    map.fitBounds(L.latLngBounds(points.map((p) => [p.lat, p.lng])), { padding: [36, 36], maxZoom: 11 });
  }, [map, points]);
  return null;
}

function ClickCapture({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(Number(e.latlng.lat.toFixed(6)), Number(e.latlng.lng.toFixed(6)));
    },
  });
  return null;
}

export default function LocationMiniMap({
  points,
  height = 340,
  onPick,
}: {
  points: MapPoint[];
  height?: number;
  /** When set, clicking the map reports coordinates (used by the Add Location modal). */
  onPick?: (lat: number, lng: number) => void;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl"
      style={{ height, border: '1px solid rgba(11,59,46,0.35)', boxShadow: '0 1px 2px rgba(11,59,46,0.06)' }}
    >
      <MapContainer
        center={points.length ? [points[0].lat, points[0].lng] : INDIA_CENTER}
        zoom={points.length ? 9 : 4}
        scrollWheelZoom={false}
        style={{ height: '100%', width: '100%', filter: 'saturate(0.8) sepia(0.08)', cursor: onPick ? 'crosshair' : undefined }}
        attributionControl
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        {points.map((p) => (
          <Marker key={p.id} position={[p.lat, p.lng]} icon={pinIcon(Boolean(p.primary))}>
            <Tooltip direction="top">
              <span className="text-xs font-semibold">{p.primary ? '★ ' : ''}{p.name}</span>
            </Tooltip>
          </Marker>
        ))}
        <FitBounds points={points} />
        {onPick ? <ClickCapture onPick={onPick} /> : null}
      </MapContainer>
    </div>
  );
}
